/**
 * Prepends product card(s) to the collection grid when the URL includes
 * ?highlight=product-handle or ?product=product-handle.
 *
 * Example:
 * /collections/women-new-arrival?highlight=summer-dress-handle
 */
(function () {
  const HIGHLIGHT_SECTION_ID = "collection-highlight-product-item";

  const highlightHandles = (function () {
    const params = new URLSearchParams(window.location.search);
    const handles = [
      ...params.getAll("highlight"),
      ...params.getAll("product"),
    ]
      .flatMap((value) => value.split(","))
      .map((handle) => handle.trim())
      .filter(Boolean);

    const seen = new Set();
    return handles.filter((handle) => {
      const key = handle.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  const fetchCache = new Map();
  let dedupePaused = false;
  let duplicateObserver = null;

  if (
    highlightHandles.length &&
    document.body.classList.contains("template-collection")
  ) {
    highlightHandles.forEach((handle) => {
      fetchCache.set(handle.toLowerCase(), fetchProductCard(handle));
    });
  }

  function getCollectionSection() {
    const container = document.querySelector('[data-section-type="collection"]');
    if (!container) return null;

    return (window.Shopify?.theme?.sections?.instances || []).find(
      (instance) => instance.container === container
    );
  }

  function getGrid() {
    return document.querySelector(".collection__infinite-container");
  }

  function getHandleFromProductItem(item) {
    const link = item.querySelector('a[href*="/products/"]');
    if (!link) return null;

    const match = link.getAttribute("href").match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function findProductInGrid(grid, handle) {
    const normalized = handle.toLowerCase();
    const items = Array.from(grid.querySelectorAll(":scope > .product-item"));

    return items.find(
      (item) => getHandleFromProductItem(item)?.toLowerCase() === normalized
    );
  }

  function getProductCardUrl(handle) {
    const collectionMatch = window.location.pathname.match(
      /\/collections\/([^/?#]+)/
    );
    const collectionSection = document.querySelector(
      '[data-section-type="collection"]'
    );
    const params = new URLSearchParams({ section_id: HIGHLIGHT_SECTION_ID });

    if (collectionSection?.dataset.collectionColumnsDesktop) {
      params.set(
        "highlight_columns_desktop",
        collectionSection.dataset.collectionColumnsDesktop
      );
    }

    if (collectionSection?.dataset.collectionColumnsMobile) {
      params.set(
        "highlight_columns_mobile",
        collectionSection.dataset.collectionColumnsMobile
      );
    }

    const base = collectionMatch
      ? "/collections/" +
        encodeURIComponent(collectionMatch[1]) +
        "/products/" +
        encodeURIComponent(handle)
      : (window.theme?.routes?.products || "/products") +
        "/" +
        encodeURIComponent(handle);

    return base + "?" + params.toString();
  }

  function fetchProductCard(handle) {
    return fetch(getProductCardUrl(handle))
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return null;

        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;
        const item = wrapper.querySelector("[data-collection-highlight-item]");

        return item ? item.innerHTML.trim() : null;
      })
      .catch(() => null);
  }

  function reinitCollectionCards(section) {
    const callback = section.infiniteScroll?.settings?.callback;
    if (typeof callback === "function") {
      callback.call(section);
    }
  }

  function whenCollectionReady(callback, attempt) {
    const section = getCollectionSection();
    if (section?.productItem) {
      callback(section);
      return;
    }

    if (attempt < 100) {
      window.setTimeout(() => whenCollectionReady(callback, attempt + 1), 25);
    }
  }

  function whenGridReady(callback) {
    const grid = getGrid();
    if (grid) {
      callback(grid);
      return;
    }

    const observer = new MutationObserver(() => {
      const nextGrid = getGrid();
      if (!nextGrid) return;

      observer.disconnect();
      callback(nextGrid);
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function prependInOrder(grid, entries) {
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const entry = entries[index];

      if (typeof entry === "string") {
        grid.insertAdjacentHTML("afterbegin", entry);
      } else {
        grid.prepend(entry);
      }
    }
  }

  function markHighlightItem(item, handle) {
    item.setAttribute("data-collection-highlight", handle);
  }

  function hideDuplicateProducts(grid) {
    if (!grid || dedupePaused) return;

    highlightHandles.forEach((handle) => {
      const normalized = handle.toLowerCase();
      const matches = Array.from(
        grid.querySelectorAll(":scope > .product-item")
      ).filter(
        (item) => getHandleFromProductItem(item)?.toLowerCase() === normalized
      );

      matches.forEach((item, index) => {
        if (index === 0) {
          markHighlightItem(item, handle);
          item.hidden = false;
          item.removeAttribute("aria-hidden");
        } else {
          item.hidden = true;
          item.setAttribute("aria-hidden", "true");
        }
      });
    });
  }

  function observeGridDuplicates(grid) {
    if (duplicateObserver) duplicateObserver.disconnect();

    duplicateObserver = new MutationObserver((mutations) => {
      if (dedupePaused) return;

      const addedProducts = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.classList?.contains("product-item") ||
              node.querySelector?.(".product-item"))
        )
      );

      if (addedProducts) {
        requestAnimationFrame(() => hideDuplicateProducts(grid));
      }
    });

    duplicateObserver.observe(grid, { childList: true });
  }

  async function prependHighlightCards(grid) {
    const handles = highlightHandles;
    if (!handles.length) return;

    const existingByHandle = new Map();
    const missing = [];

    handles.forEach((handle) => {
      const item = findProductInGrid(grid, handle);
      if (item) {
        existingByHandle.set(handle.toLowerCase(), item);
      } else {
        missing.push(handle);
      }
    });

    dedupePaused = true;

    try {
      const existingEntries = handles
        .map((handle) => existingByHandle.get(handle.toLowerCase()))
        .filter(Boolean);

      if (existingEntries.length) {
        prependInOrder(grid, existingEntries);
      }

      if (missing.length) {
        const fetched = await Promise.all(
          missing.map((handle) => fetchCache.get(handle.toLowerCase()))
        );
        const toInject = [];
        let injectedAny = false;

        missing.forEach((handle, index) => {
          const html = fetched[index];
          if (!html) return;

          toInject.push({ handle, html });
          injectedAny = true;
        });

        if (toInject.length) {
          for (let index = toInject.length - 1; index >= 0; index -= 1) {
            grid.insertAdjacentHTML("afterbegin", toInject[index].html);
          }

          if (injectedAny) {
            whenCollectionReady(reinitCollectionCards);
          }
        }
      }
    } finally {
      dedupePaused = false;
    }

    hideDuplicateProducts(grid);
  }

  function init() {
    if (!document.body.classList.contains("template-collection")) return;
    if (!highlightHandles.length) return;

    whenGridReady((grid) => {
      observeGridDuplicates(grid);
      prependHighlightCards(grid);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
